import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const SESSION_KEY = "br_session_token";
const USER_KEY = "br_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check existing session on mount
    const token = localStorage.getItem(SESSION_KEY);
    const cached = localStorage.getItem(USER_KEY);

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {}
    }

    if (token) {
      axios
        .get("/api/auth/user/me", {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(localStorage.getItem("br_view_as")
              ? { "X-View-As": localStorage.getItem("br_view_as") }
              : {}),
          },
        })
        .then((res) => {
          setUser(res.data);
          localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        })
        .catch((err) => {
          // Only a real "session invalid" signs out. A network blip (phone
          // waking up, weak signal) keeps the cached user and token.
          const status = err?.response?.status;
          if (status === 401 || !cached) {
            localStorage.removeItem(SESSION_KEY);
            localStorage.removeItem(USER_KEY);
            setUser(null);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setUser(null);
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem(SESSION_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
  };

  const logout = async () => {
    localStorage.removeItem("br_view_as");
    localStorage.removeItem("br_view_as_name");
    const token = localStorage.getItem(SESSION_KEY);
    if (token) {
      try {
        await axios.post("/api/auth/user/logout", null, { headers: { Authorization: `Bearer ${token}` } });
      } catch {}
    }
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Like useAuth, but returns null outside AuthProvider instead of throwing. */
export function useOptionalAuth() {
  return useContext(AuthContext);
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
